/**
 * <ry-modal>
 *
 * Modal dialog with focus trapping and backdrop.
 *
 * Minimal markup (recommended):
 * <ry-button modal="my-modal">Open</ry-button>
 * <ry-modal id="my-modal" title="Title">
 *   Content
 *   <footer>...</footer>
 * </ry-modal>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

export class RyModal extends RyElement {
  #previouslyFocused: Element | null = null;
  #scrollbarWidth = 0;

  setup(): void {
    // Handle Option B: transform minimal markup
    this.#transformMinimalMarkup();

    // Find trigger buttons anywhere in document
    const modalId = this.id;
    if (modalId) {
      // data-ry-modal-trigger attribute
      document.querySelectorAll<HTMLElement>(`[data-ry-modal-trigger="${modalId}"]`).forEach(trigger => {
        this.on(trigger, 'click', () => this.open());
      });

      // modal="id" attribute on ry-button
      document.querySelectorAll<HTMLElement>(`ry-button[modal="${modalId}"], [modal="${modalId}"]`).forEach(trigger => {
        this.on(trigger, 'click', () => this.open());
      });
    }

    // Close buttons inside modal (use [close] attribute)
    this.$$('[close]').forEach(closeBtn => {
      this.on(closeBtn, 'click', () => this.close());
    });

    // Backdrop click
    const backdrop = this.$('[data-ry-target="backdrop"]');
    if (backdrop) {
      this.on(backdrop, 'click', () => this.close());
    }

    // Escape key
    this.on(document, 'keydown', this.#handleKeydown);

    // Calculate scrollbar width
    this.#scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Initialize state
    if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }
  }

  #transformMinimalMarkup(): void {
    // Check if already has full markup
    if (this.$('[data-ry-target="dialog"]')) return;

    const title = this.getAttribute('title') ?? '';
    const footerEl = this.$<HTMLElement>('footer');
    const footerContent = footerEl?.innerHTML ?? '';

    const bodyContent = Array.from(this.childNodes)
      .filter(n => !(n instanceof HTMLElement && n.tagName === 'FOOTER'))
      .map(n => n instanceof Element ? n.outerHTML : n.textContent)
      .join('');

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="backdrop" class="ry-modal__backdrop"></div>
      <div data-ry-target="dialog" class="ry-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="ry-modal-title-${this.id}">
        <div data-ry-target="header" class="ry-modal__header">
          <h3 class="ry-modal__title" id="ry-modal-title-${this.id}">${title}</h3>
          <button data-ry-target="close" class="ry-modal__close" close aria-label="Close">&times;</button>
        </div>
        <div data-ry-target="body" class="ry-modal__body">${bodyContent}</div>
        ${footerContent ? `<div data-ry-target="footer" class="ry-modal__footer">${footerContent}</div>` : ''}
      </div>
    `;
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state === 'open') {
      this.close();
    }
  };

  open(): void {
    if (this.state === 'open') return;

    // Store currently focused element
    this.#previouslyFocused = document.activeElement;

    // Lock body scroll via data attribute (CSS handles the style)
    document.body.setAttribute('data-ry-scroll-lock', '');
    document.body.style.setProperty('--ry-scrollbar-width', `${this.#scrollbarWidth}px`);

    // Open modal
    this.state = 'open';

    // Trap focus
    const dialog = this.$<HTMLElement>('[data-ry-target="dialog"]');
    if (dialog) {
      this.nextFrame().then(() => {
        this.trapFocus(dialog);
      });
    }

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;

    // Unlock body scroll
    document.body.removeAttribute('data-ry-scroll-lock');
    document.body.style.removeProperty('--ry-scrollbar-width');

    // Close modal
    this.state = 'closed';

    // Restore focus
    if (this.#previouslyFocused instanceof HTMLElement) {
      this.#previouslyFocused.focus();
      this.#previouslyFocused = null;
    }

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }
}

customElements.define('ry-modal', RyModal);
